import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { uidSchema, type Board, type Match } from '@/core/schemas';
import { useAuth } from '@/app/providers/auth/authContext';
import { describeError } from '@/features/team/describeError';
import { MATCH_STATUS_LABELS, scoreLabel, sideLabel } from '@/features/matches/matchPresentation';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import { setBoardStatus } from '@/infrastructure/repositories/boardRepository';
import { assignBoard, callMatch } from '@/infrastructure/repositories/matchRepository';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

import { useBoards } from './useBoards';

/**
 * Board management for the director: assign a board and call a match up. The
 * result itself is entered by the players at the board via the live counter —
 * this page never touches the score.
 */
export function AdminMatchesPage() {
  usePageTitle('Spiele & Boards');
  const { session } = useAuth();
  const { tournament, matches, lookup, isLoading, errorMessage } = useTournamentData();
  const boards = useBoards();
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const [boardError, setBoardError] = useState<string | null>(null);

  const actorUid = session.status === 'authenticated' ? uidSchema.parse(session.account.uid) : null;

  const call = useMutation({
    mutationFn: (input: { match: Match; boardNumber: number }) => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      return callMatch({ ...input, actorUid });
    },
  });

  const board = useMutation({
    mutationFn: (input: { match: Match; boardNumber: number | null }) => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      return assignBoard({ ...input, actorUid });
    },
  });

  const boardStatus = useMutation({
    mutationFn: (input: { board: Board; disable: boolean }) => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      return setBoardStatus({
        board: input.board,
        status: input.disable ? 'disabled' : 'free',
        actorUid,
      });
    },
  });

  if (isLoading) {
    return <LoadingScreen label="Spiele werden geladen …" />;
  }

  if (errorMessage !== null || tournament === undefined) {
    return <PageState title="Spiele nicht verfügbar" description={errorMessage ?? ''} />;
  }

  if (matches.length === 0) {
    return <Alert tone="info">Es gibt noch keine Spiele — das Turnier ist nicht gestartet.</Alert>;
  }

  const phases = tournament.settings.phases;
  const boardCount = tournament.settings.general.boardCount;
  const disabledBoardNumbers = new Set(
    (boards.data ?? []).filter((b) => b.status === 'disabled').map((b) => b.number),
  );
  const visible =
    phaseFilter === 'all' ? matches : matches.filter((m) => m.phaseId === phaseFilter);
  const error =
    boardError ??
    describeError(call.error) ??
    describeError(board.error) ??
    describeError(boardStatus.error);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Spiele & Boards ({visible.length})</h2>
        <select
          value={phaseFilter}
          onChange={(event) => setPhaseFilter(event.target.value)}
          className="bg-surface-raised border-line rounded-lg border px-3 py-2 text-sm"
        >
          <option value="all">Alle Phasen</option>
          {phases.map((phase) => (
            <option key={phase.id} value={phase.id}>
              {phase.label}
            </option>
          ))}
        </select>
      </div>

      {error !== null && <Alert tone="danger">{error}</Alert>}

      {boards.data !== undefined && boards.data.length > 0 && (
        <section className="border-line bg-surface flex flex-col gap-3 rounded-xl border p-4">
          <div className="flex items-baseline justify-between">
            <h3 className="text-sm font-semibold tracking-wide uppercase">Boards</h3>
            <p className="text-fg-subtle text-xs">
              Gesperrte Boards werden bei der Zuweisung abgelehnt.
            </p>
          </div>
          <ul className="flex flex-wrap gap-2">
            {boards.data.map((b) => {
              const isDisabled = b.status === 'disabled';
              return (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => boardStatus.mutate({ board: b, disable: !isDisabled })}
                    disabled={boardStatus.isPending}
                    className={
                      isDisabled
                        ? 'border-danger/50 bg-danger-subtle text-danger flex items-center gap-2 rounded-lg border px-3 py-2 text-sm'
                        : 'border-line bg-surface-raised text-fg flex items-center gap-2 rounded-lg border px-3 py-2 text-sm'
                    }
                    title={isDisabled ? 'Klicken, um freizugeben' : 'Klicken, um zu sperren'}
                  >
                    <span className="font-semibold">Board {b.number}</span>
                    <span className="text-fg-subtle text-xs">
                      {isDisabled ? 'gesperrt' : b.status === 'occupied' ? 'belegt' : 'frei'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <ul className="flex flex-col gap-2">
        {visible.map((match) => {
          const ready = match.home.kind === 'team' && match.away.kind === 'team';
          const open = match.status !== 'completed';

          return (
            <li
              key={match.id}
              className="border-line bg-surface flex flex-wrap items-center gap-3 rounded-lg border p-3"
            >
              <span className="min-w-0 flex-1 truncate text-sm">
                {sideLabel(match.home, lookup)}{' '}
                <span className="text-fg-subtle font-mono">
                  {scoreLabel(match) === '' ? 'vs' : scoreLabel(match)}
                </span>{' '}
                {sideLabel(match.away, lookup)}
              </span>

              <span className="text-fg-subtle text-xs">{MATCH_STATUS_LABELS[match.status]}</span>

              {ready && open && (
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <span className="text-fg-subtle text-xs uppercase">Board</span>
                    <input
                      type="number"
                      min={1}
                      max={boardCount}
                      defaultValue={match.boardNumber ?? ''}
                      onBlur={(event) => {
                        const value = event.target.value.trim();
                        const next = value === '' ? null : Number(value);
                        if (next !== null && (next < 1 || next > boardCount)) {
                          setBoardError(
                            `Es gibt nur ${boardCount} Boards. Bitte 1 bis ${boardCount} eintragen.`,
                          );
                          event.target.value = String(match.boardNumber ?? '');
                          return;
                        }
                        if (next !== null && disabledBoardNumbers.has(next)) {
                          setBoardError(
                            `Board ${next} ist gesperrt und kann nicht zugewiesen werden.`,
                          );
                          event.target.value = String(match.boardNumber ?? '');
                          return;
                        }
                        setBoardError(null);
                        if (next !== match.boardNumber) {
                          board.mutate({ match, boardNumber: next });
                        }
                      }}
                      className="bg-surface-raised border-line w-16 rounded-lg border px-2 py-1.5 text-center"
                    />
                  </label>

                  {match.boardNumber !== null && match.status === 'pending' && (
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => call.mutate({ match, boardNumber: match.boardNumber ?? 1 })}
                      isLoading={call.isPending}
                    >
                      Aufrufen
                    </Button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <p className="text-fg-subtle text-sm">
        Ergebnisse tragen die Teams am Board über den Live-Counter ein. Ein angezweifeltes Ergebnis
        erscheint unter „Konflikte".
      </p>
    </div>
  );
}
